import { getLabelMessage } from '@gef-ui/localization/messages';
import { partial } from 'filesize';
import PropTypes from 'prop-types';
import path from "ramda/es/path";
import T from "ramda/es/T";
import always from "ramda/es/always";
import compose from "ramda/es/compose";
import cond from "ramda/es/cond";
import isNil from "ramda/es/isNil";
import map from "ramda/es/map";
import not from "ramda/es/not";
import pathEq from "ramda/es/pathEq";
import prop from "ramda/es/prop";
import propEq from "ramda/es/propEq";
import reject from "ramda/es/reject";
import test from "ramda/es/test";
import cx from "ramda-extension/es/cx";
import React, { Component, createElement } from 'react';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import CollapseValidation from '../../../molecules/Validation/CollapseValidation';
import Button from '../../Button';
import Icon from '../../Icon';
const COMPONENT_NAME = 'FileInput';
const filePath = path([
    'files'
]);
const fileName = prop('name');
const humanFileSize = partial({
    base: 10,
    round: 1
});
const alwaysError = (file, msg)=>({
        valid: false,
        file,
        error: new Error(msg)
    });
class FileInput extends Component {
    constructor(props){
        super(props);
        this.state = {
            files: []
        };
        this.id = props.id || crypto.randomUUID();
    }
    componentDidMount() {
        if (process.env.NODE_ENV === 'development') {
            console.warn('FileInput have been deprecated and it will be removed in future. Please use FileInputV2');
        }
    }
    handleRemoveFile = (file)=>()=>{
            const fileInState = pathEq(file.name, [
                'file',
                'name'
            ]);
            const files = reject(fileInState, this.state.files);
            this.setState({
                files
            }, ()=>this.props.handleFileChange?.(null, files));
        };
    buildDefaultFileMessage = ({ file, error })=>{
        const rowClasses = cx('FileInput-row', {
            'FileInput-row--invalid': error
        }, {
            'FileInput-row--valid': !error
        });
        return /*#__PURE__*/ React.createElement("div", {
            className: rowClasses,
            key: file.name
        }, /*#__PURE__*/ React.createElement("span", {
            className: "FileInput-name"
        }, file.name), /*#__PURE__*/ React.createElement("span", {
            className: "FileInput-size"
        }, humanFileSize(file.size)), error ? /*#__PURE__*/ React.createElement("span", {
            className: "FileInput-error"
        }, error.message) : null, /*#__PURE__*/ React.createElement("button", {
            className: "FileInput-remove",
            onClick: this.handleRemoveFile(file)
        }, "✖"));
    };
    fileMessage = (fileItem)=>this.buildDefaultFileMessage(fileItem);
    errorMessage = (fileItem)=>this.buildDefaultFileMessage(fileItem);
    handleFileChange = (e)=>{
        const { acceptedFileTypeMessage, acceptedFileTypes, disabled, maxSizeMessage, maxSize, handleFileChange } = this.props;
        if (disabled) {
            return;
        }
        const checkAcceptedFileTypes = compose(not, test(acceptedFileTypes), fileName);
        const checkFileSize = (file)=>maxSize ? maxSize < file.size : false;
        const checkFileValidity = cond([
            [
                isNil,
                always(null)
            ],
            [
                checkAcceptedFileTypes,
                (f)=>alwaysError(f, acceptedFileTypeMessage)
            ],
            [
                checkFileSize,
                (f)=>alwaysError(f, maxSizeMessage)
            ],
            [
                T,
                (f)=>({
                        valid: true,
                        file: f
                    })
            ]
        ]);
        const files = map(checkFileValidity, filePath(e.target));
        this.setState({
            files
        });
        handleFileChange?.(e, files);
        if (this.fileInput) {
            this.fileInput.value = '';
        }
    };
    displayFileMessages(files) {
        return map(cond([
            [
                isNil,
                always('')
            ],
            [
                propEq(false, 'valid'),
                this.errorMessage
            ],
            [
                T,
                this.fileMessage
            ]
        ]))(files);
    }
    render() {
        const { accept, customButton, disabled, className, multiple, customFileMessage, hideValidationText, validationText, validationClassName } = this.props;
        const { files } = this.state;
        const fileMessage = customFileMessage ? /*#__PURE__*/ createElement(customFileMessage, {
            files,
            removeFile: this.handleRemoveFile
        }) : this.displayFileMessages(files);
        return /*#__PURE__*/ React.createElement("div", {
            className: cx('FileInput', className)
        }, /*#__PURE__*/ React.createElement("input", {
            className: "FileInput-input",
            type: "file",
            ref: (ref)=>{
                this.fileInput = ref;
            },
            accept: accept,
            onChange: this.handleFileChange,
            disabled: disabled,
            multiple: multiple,
            id: this.id
        }), /*#__PURE__*/ React.createElement("label", {
            className: "FileInput-control",
            htmlFor: this.id
        }, customButton ? customButton : /*#__PURE__*/ React.createElement(Button, {
            className: "FileInput-button",
            containerElement: "span",
            icon: /*#__PURE__*/ React.createElement(Icon, {
                name: "upload"
            }),
            primary: true,
            disabled: disabled,
            label: ""
        })), /*#__PURE__*/ React.createElement(CollapseValidation, {
            hideValidationText: hideValidationText,
            validationText: validationText,
            validationClassName: validationClassName
        }), fileMessage);
    }
}
FileInput.propTypes = {
    /**
	 * html accept attribute, see https://www.w3schools.com/TAGs/att_input_accept.asp
	 */ accept: PropTypes.string,
    /**
	 * Error message when don't match
	 */ acceptedFileTypeMessage: PropTypes.string,
    /**
	 * reqexp for accepted file types. eg. <code>/(\.&#124;\/)(gif&#124;jpe?g&#124;png&#124;bmp)$/i</code>
	 */ acceptedFileTypes (props, propName, componentName) {
        if (!(props[propName] instanceof RegExp)) {
            return new Error(`Invalid prop '${propName}' supplied to '${componentName}'.
			 Validation failed, should be type of RegExp `);
        }
    },
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * Custom Button component
	 */ customButton: PropTypes.node,
    /**
	 * Custom file message function
	 *
	 * @return {Element} HTML Element
	 */ customFileMessage: PropTypes.func,
    /**
	 * If true, the select field will be disabled.
	 */ disabled: PropTypes.bool,
    /**
	 * Callback function fired when file is selected
	 *
	 * @param {object} event event targeting the element.
	 * @param {File} file object. https://developer.mozilla.org/cs/docs/Web/API/File
	 * @return {undefined} Nothin'
	 */ handleFileChange: PropTypes.func,
    /**
	 * Whether to render validation component.
	 */ hideValidationText: PropTypes.bool,
    /**
	 * Id of the form element . If not specified, one will be generated automatically to connect
	 * input with label using the `for` attribute.
	 */ id: PropTypes.string,
    /**
	 * Max size of vile
	 */ maxSize: PropTypes.number,
    /**
	 * Error message when size is exceeded
	 */ maxSizeMessage: PropTypes.string,
    /**
	 * If true, more files can be selected.
	 */ multiple: PropTypes.bool,
    /**
	 * Set class for validation component.
	 */ validationClassName: PropTypes.string,
    /**
	 * Text that will be shown when withValidity decorator decides to show validation text.
	 */ validationText: PropTypes.node
};
export { FileInput };
export default withDefaultComponentProps(COMPONENT_NAME, {
    disabled: false,
    acceptedFileTypes: /.*/,
    acceptedFileTypeMessage: getLabelMessage('validation:wrongExtension'),
    maxSizeMessage: getLabelMessage('validation:wrongSize'),
    multiple: false
}, FileInput);
